<script lang="ts" setup>
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeMultiSelect from "@dsk/components/Form/LeMultiSelect.vue";
import LeTextarea from "@dsk/components/Form/LeTextarea.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeLabel from "@dsk/components/Label/LeLabel.vue";
import LeModal from "@dsk/components/LeModal.vue";
import LeToggle from "@dsk/components/LeToggle.vue";
import type { ModalProps } from "@dsk/types";
import { useField, useFieldArray, useForm } from "vee-validate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { ValidationHelper } from "@/helpers";
import { BusinessSubscriptionRequestService } from "@/services";
import type { BusinessSubscriptionType, LeSelectOption } from "@/types";
import {
  type BusinessSubscriptionRequest,
  businessSubscriptionRequestFormValidator,
  businessSubscriptionRequestLanguages,
  businessSubscriptionRequestSubjects
} from "@/validation";

const props = defineProps<
  ModalProps & {
    subscriptionType?: BusinessSubscriptionType;
  }
>();

const emit = defineEmits<{
  close: [didSubmit: boolean];
}>();

const { t } = useI18n();

const selectedSubscriptionTexts: Record<
  BusinessSubscriptionType,
  {
    label: {
      text: string;
      color: "default" | "attention";
    };
    text: string;
  }
> = {
  individual: {
    label: {
      text: t("le-business-subscription-request-modal.selected-subscription-label-individual"),
      color: "default"
    },
    text: t("le-business-subscription-request-modal.selected-subscription-text-individual")
  },
  flexible: {
    label: {
      text: t("le-business-subscription-request-modal.selected-subscription-label-flexible"),
      color: "attention"
    },
    text: t("le-business-subscription-request-modal.selected-subscription-text-flexible")
  },
  professional: {
    label: {
      text: t("le-business-subscription-request-modal.selected-subscription-label-professional"),
      color: "default"
    },
    text: ""
  }
};

const { handleSubmit, errors, resetForm } = useForm({
  initialValues: {
    type: props.subscriptionType,
    phone: "",
    accountQty: "",
    languages: [],
    subjects: [],
    specializedDictionaries: false,
    comment: ""
  },
  validationSchema: businessSubscriptionRequestFormValidator
});

const languageOptions: LeSelectOption<string>[] = Object.entries(businessSubscriptionRequestLanguages).map(
  ([key, value]) => ({ value: key, text: value })
);

const subjectOptions: LeSelectOption<string>[] = Object.entries(businessSubscriptionRequestSubjects).map(
  ([key, value]) => ({ value: key, text: value })
);

const { value: name, meta: nameMeta } = useField<string>("name");
const { value: email, meta: emailMeta } = useField<string>("email");
const { value: phone, meta: phoneMeta } = useField<string>("phone");

const { value: company, meta: companyMeta } = useField<string>("company");
const { value: accountQty, meta: accountQtyMeta } = useField<string>("accountQty");
const { value: specializedDictionaries } = useField<boolean>("specializedDictionaries");

const languages = useFieldArray<string>("languages");
const languagesModel = computed<string[]>({
  get() {
    return languages.fields.value.map((l) => l.value);
  },
  set(value: string[]) {
    languages.replace(value);
  }
});

const subjects = useFieldArray<string>("subjects");
const subjectsModel = computed<string[]>({
  get() {
    return subjects.fields.value.map((s) => s.value);
  },
  set(value: string[]) {
    subjects.replace(value);
  }
});

const { value: comment, meta: commentMeta } = useField<string>("comment");

const hasApiError = ref<boolean>(false);
const onSubmit = handleSubmit(async (form) => {
  hasApiError.value = false;

  try {
    await BusinessSubscriptionRequestService.submit(form as BusinessSubscriptionRequest);

    onClose(true);
  } catch (error) {
    hasApiError.value = true;
  }
});

const onClose = (didSubmit: boolean) => {
  resetForm();
  emit("close", didSubmit);
};
</script>

<template>
  <LeModal class="le-business-subscription-request-modal" :is-open="isOpen" @close="onClose(false)">
    <template #title>
      <LeHeading tag="h1" size="m">{{ t("le-business-subscription-request-modal.title") }}</LeHeading>
    </template>

    <template #body>
      <div v-if="subscriptionType" class="le-business-subscription-request-modal__selected-subscription-info">
        <span>
          {{ t("le-business-subscription-request-modal.selected-subscription") }}
        </span>
        <LeLabel size="l" :color="selectedSubscriptionTexts[subscriptionType].label.color">{{
          selectedSubscriptionTexts[subscriptionType].label.text
        }}</LeLabel>
        <span>
          {{ selectedSubscriptionTexts[subscriptionType].text }}
        </span>
      </div>

      <form
        id="business-subscription-request-form"
        class="le-business-subscription-request-modal__form"
        :class="{ 'le-business-subscription-request-modal__form--no-type': subscriptionType === undefined }"
        @submit.prevent="onSubmit()"
      >
        <div class="row le-business-subscription-request-modal__input-groups-container">
          <div v-if="hasApiError" class="col-xs-12" role="alert">
            <p class="le-business-subscription-request-modal__alert">
              {{ t("le-business-subscription-request-modal.form.api-error") }}
            </p>
          </div>
          <div class="col-xs-12 col-sm-6">
            <LeHeading tag="h2" size="s" class="le-business-subscription-request-modal__form-heading">
              {{ t("le-business-subscription-request-modal.form.about-you-heading") }}
            </LeHeading>
            <div class="le-business-subscription-request-modal__form-group">
              <LeInput
                v-model="name"
                :label="t('le-business-subscription-request-modal.form.name.label')"
                :placeholder="t('le-business-subscription-request-modal.form.name.placeholder')"
                :has-error="ValidationHelper.displayError(nameMeta)"
              />
              <small v-if="errors.name" class="le-business-subscription-request-modal__form-error-text">{{
                errors.name
              }}</small>
            </div>
            <div class="le-business-subscription-request-modal__form-group">
              <LeInput
                v-model="email"
                :label="t('le-business-subscription-request-modal.form.email.label')"
                :placeholder="t('le-business-subscription-request-modal.form.email.placeholder')"
                :has-error="ValidationHelper.displayError(emailMeta)"
              />
              <small v-if="errors.email" class="le-business-subscription-request-modal__form-error-text">{{
                errors.email
              }}</small>
            </div>
            <div class="le-business-subscription-request-modal__form-group">
              <LeInput
                v-model="phone"
                :label="t('le-business-subscription-request-modal.form.phone.label')"
                :placeholder="t('le-business-subscription-request-modal.form.phone.placeholder')"
                :has-error="ValidationHelper.displayError(phoneMeta)"
              />
              <small v-if="errors.phone" class="le-business-subscription-request-modal__form-error-text">{{
                errors.phone
              }}</small>
            </div>
          </div>
          <div class="col-xs-12 col-sm-6">
            <LeHeading tag="h2" size="s" class="le-business-subscription-request-modal__form-heading">
              {{ t("le-business-subscription-request-modal.form.about-company-heading") }}
            </LeHeading>
            <div class="le-business-subscription-request-modal__form-group">
              <LeInput
                v-model="company"
                :label="t('le-business-subscription-request-modal.form.company.label')"
                :placeholder="t('le-business-subscription-request-modal.form.company.placeholder')"
                :has-error="ValidationHelper.displayError(companyMeta)"
              />
              <small v-if="errors.company" class="le-business-subscription-request-modal__form-error-text">{{
                errors.company
              }}</small>
            </div>
            <div class="le-business-subscription-request-modal__form-group">
              <LeInput
                v-model="accountQty"
                type="number"
                min="1"
                :label="t('le-business-subscription-request-modal.form.account-qty.label')"
                :placeholder="t('le-business-subscription-request-modal.form.account-qty.placeholder')"
                :has-error="ValidationHelper.displayError(accountQtyMeta)"
              />
              <small v-if="errors.accountQty" class="le-business-subscription-request-modal__form-error-text">{{
                errors.accountQty
              }}</small>
            </div>
            <div
              class="le-business-subscription-request-modal__form-group le-business-subscription-request-modal__specialized-dictionaries"
            >
              <p class="le-business-subscription-request-modal__text">
                {{ t("le-business-subscription-request-modal.form.specialized-dictionaries.label") }}
              </p>
              <LeToggle v-model="specializedDictionaries" />
            </div>
          </div>
          <div class="col-xs-12">
            <LeHeading
              tag="h2"
              size="s"
              class="le-business-subscription-request-modal__form-heading le-business-subscription-request-modal__form-heading--top-spacing"
            >
              {{ t("le-business-subscription-request-modal.form.needs-heading") }}
            </LeHeading>
            <div class="le-business-subscription-request-modal__form-group">
              <LeMultiSelect
                v-model="languagesModel"
                width="100%"
                max-height="300px"
                :label="t('le-business-subscription-request-modal.form.languages.label')"
                :placeholder="t('le-business-subscription-request-modal.form.languages.placeholder')"
                :drop-up="true"
                :options="languageOptions"
              />
            </div>
            <div v-if="specializedDictionaries" class="le-business-subscription-request-modal__form-group">
              <LeMultiSelect
                v-model="subjectsModel"
                width="100%"
                max-height="300px"
                :drop-up="true"
                :label="t('le-business-subscription-request-modal.form.subjects.label')"
                :placeholder="t('le-business-subscription-request-modal.form.subjects.placeholder')"
                :options="subjectOptions"
              />
            </div>
          </div>
          <div class="col-xs-12">
            <div
              class="le-business-subscription-request-modal__form-group le-business-subscription-request-modal__form-group--top-spacing"
            >
              <LeTextarea
                v-model="comment"
                class="le-business-subscription-request-modal__form-comment"
                :label="t('le-business-subscription-request-modal.form.comment.label')"
                :placeholder="t('le-business-subscription-request-modal.form.comment.placeholder')"
                rows="1"
                :has-error="ValidationHelper.displayError(commentMeta)"
              />
              <small v-if="errors.comment" class="le-business-subscription-request-modal__form-error-text">{{
                errors.comment
              }}</small>
            </div>
          </div>
          <div class="col-xs-12">
            <p class="le-business-subscription-request-modal__required-info-text">
              {{ t("le-business-subscription-request-modal.form.required-info-text") }}
            </p>
          </div>
        </div>
      </form>
    </template>

    <template #buttons>
      <LeButton variant="outline" @click="onClose(false)">
        {{ t("le-business-subscription-request-modal.footer.cancel-btn") }}
      </LeButton>
      <LeButton type="submit" form="business-subscription-request-form">
        {{ t("le-business-subscription-request-modal.footer.submit-btn") }}
      </LeButton>
    </template>
  </LeModal>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-business-subscription-request-modal {
  &__alert {
    width: 100%;
    margin-bottom: 12px;
    padding: 12px 16px;
    border: none;
    background-color: @colors[@error][200];
    .body-text("s");
    color: @colors[@error][900];
  }

  &__text {
    margin-bottom: 0;
    .body-text("m");
  }

  &__required-info-text {
    margin-top: 16px;
    margin-bottom: 0;
    .body-text("s");
    color: @colors[@neutrals][200];
  }

  &__selected-subscription-info {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    .body-text("m", "semi-bold");

    > span:nth-child(1) {
      width: 100%;

      #media-breakpoint-up(md, {
        width: auto;
      });
    }

    #media-breakpoint-up(md, {
      display: flex;
      align-items: center;
    });
  }

  &__form {
    margin-top: 20px;
    margin-bottom: 30px;

    &--no-type {
      margin-top: 10px;
    }

    #media-breakpoint-up(md, {
      margin-bottom: 20px;
    });

    :deep(&-comment) {
      max-height: 100px;
    }
  }

  &__input-groups-container {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: -18px; // Offset for ::before element
    margin-bottom: -18px; // Offset for ::after element

    #media-breakpoint-up(md, {
      display: block;
      margin-top: 0;
      margin-bottom: 0;
    });
  }

  &__form-heading {
    margin-bottom: 12px;

    &--top-spacing {
      #media-breakpoint-up(md, {
        margin-top: 18px;
      });
    }
  }

  &__form-group {
    margin-bottom: 12px;

    &--top-spacing {
      #media-breakpoint-up(md, {
        margin-top: 12px;
      });
    }
  }

  &__form-error-text {
    .body-text("xs");
    color: @colors[@error][600];
  }

  &__specialized-dictionaries {
    display: flex;
    justify-content: space-between;
    gap: 4px;
    padding-top: 6px;

    #media-breakpoint-up(md, {
      padding-top: 18px;
    });
  }
}
</style>
